import axios from "@/util/axios";
import {VuexActionContext} from "@/store/index";
import {User} from "@/interfaces/entities/User";
import {Address} from "@/interfaces/entities/Address";
import {UpdateUserRequest} from "@/interfaces/httpRequests/UpdateUserRequest";
import { Account } from "@/interfaces/entities/Account";
import { GetAccountDetailsResponse } from "@/interfaces/httpResponses/GetAccountDetailsResponse";
import { InviteUserRequest } from "@/interfaces/httpRequests/InviteUserRequest";
import { InvitedUser } from "@/interfaces/entities/InvitedUser";
import { UpdateMemberRequest } from "@/interfaces/httpRequests/UpdateMemberRequest";
import { Invitation } from "@/interfaces/entities/Invitation";

export interface UserState {
    user: User;
    account: Account;
}

export const userState: UserState = {
    user: {
        firstName: "",
        lastName: "",
        email: "",
        companyName: "",
        taxId: "",
        addresses: []
    },

    account:{
        id: "",
        login: "",
        firstName: "",
        lastName: "",
        email: "",
        imageUrl: "",
        activated: false,
        langKey: "",
        createdBy: "",
        createdDate: "",
        lastModifiedBy: "",
        lastModifiedDate: "",
        authorities: [],
        agentId: "",
        customerEmail: "",
        companyName: "",
        realmName: ""
    }
};

export const userMutations = {
    SET_ACCOUNT_DETAILS(state: UserState, user: User) {
        state.user = user;
    },

    SET_ACCOUNT(state: UserState, account: Account) {
        state.account = account;
    },

    UPDATE_ACCOUNT_DETAILS(state: UserState, account: Partial<Account>) {
        state.account = { ...state.account, ...account };
    }
};

export const userActions = {

    /**
     * After login on Keycloak we need to call this in order to get the user
     * on the equity server.
     */
    async FETCH_ACCOUNT({commit}: VuexActionContext<Account>): Promise<void> {
        const response: GetAccountDetailsResponse = await axios.get("/account");
        console.log("[userStore] Got account: ", response.data);
        commit("SET_ACCOUNT", response.data);
    },

    async FETCH_ACCOUNT_DETAILS({commit}: VuexActionContext<Account>): Promise<User> {
        const response = await axios.get("/users/current");
        commit("SET_ACCOUNT_DETAILS", response.data);
        return response.data;
    },

    async UPDATE_ACCOUNT(context: VuexActionContext<UserState>, payload: UpdateUserRequest): Promise<UserState> {
        return await axios.put("/users/current", payload);
    },

    async UPDATE_PROFILE({commit}: VuexActionContext<Account>, payload: UpdateUserRequest): Promise<void> {
        const response = await axios.patch("/users/current", payload);
        commit("UPDATE_ACCOUNT_DETAILS", response.data);
    },

    async FETCH_TEAM_MEMBERS(): Promise<InvitedUser[]> {
        const response = await axios.get("/users/members");
        return response.data;
    },

    async INVITE_TEAM_MEMBER(context: VuexActionContext<UserState>, payload: InviteUserRequest): Promise<void> {
        const response = await axios.post("/users/members", payload);
        return response.data;
    },

    async RESEND_INVITATION(context: VuexActionContext<UserState>, id: string): Promise<void> {
        const response = await axios.post("/users/members/" + id + "/resend-invitation");
        return response.data;
    },

    async RESET_PASSWORD(context: VuexActionContext<UserState>, id: string): Promise<void> {
        const response = await axios.post("/users/members/" + id + "/reset-password");
        return response.data;
    },

    async UPDATE_TEAM_MEMBER(context: VuexActionContext<UserState>, payload: UpdateMemberRequest): Promise<void> {
        const response = await axios.patch("/users/members", payload);
        return response.data;
    },

    async REMOVE_TEAM_MEMBER(context: VuexActionContext<UserState>, id: string): Promise<void> {
        return await axios.delete("/users/members/" + id);
    },

    async FETCH_INVITATION(context: VuexActionContext<UserState>, token: string): Promise<Invitation> {
        const response = await axios.get("/users/invitations?token=" + token);
        return response.data;
    },

    async ACCEPT_INVITATION(context: VuexActionContext<UserState>, token: string): Promise<void> {
        const response = await axios.post("/users/invitations?token=" + token);
        return response.data;
    },

    async DOWNLOAD_PACKAGES_INFO_CSV(): Promise<void> {
        const response = await axios.get("/users/current/packages-info/csv", {
            responseType: "blob"
        });

        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });

        let filename = "package-info.csv";
        const disposition = response.headers["content-disposition"];
        if (disposition) {
            const match = disposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
                filename = match[1];
            }
        }

        // Create a temporary link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);

        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
    }
};

export const userGetters = {
    hasRole: (state: UserState) => (role: string) => {
        return state.account.authorities.includes(role);
    },
    hasAnyRole: (state: UserState) => (roles: string[]) => {
        if (roles.length === 0) return true;
        return roles.some((role: string) => state.account.authorities.includes(role));
    }
};